import { Sparkles, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/common/hooks/useAuth";
import { usePermissions } from "@/common/hooks/usePermissions";
import { useSubscription } from "@/common/services/billing";

const DISMISS_KEY = "pay-nudge-dismissed-session";
/**
 * Set to "1" in sessionStorage whenever a test case POST succeeds.
 * The banner only renders once this flag is present so first-run users
 * don't see an upgrade prompt before they've engaged with the product.
 */
const SAW_FIRST_TESTCASE_KEY = "pay-nudge-show-after-testcase";

/**
 * Sticky top banner shown to authenticated users on the free plan after they
 * have saved at least one test case in the current session. Dismissable via
 * sessionStorage so it returns at the start of each new session.
 */
const UpgradeNudgeBanner = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { isAdmin: isOrgAdmin } = usePermissions();
  const { data } = useSubscription({ enabled: !!isAuthenticated });
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.sessionStorage.getItem(DISMISS_KEY) === "1";
  });
  const [hasShownNudge, setHasShownNudge] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.sessionStorage.getItem(SAW_FIRST_TESTCASE_KEY) === "1";
  });

  // Listen for a custom event so the test-case creation flow can flip the flag.
  useEffect(() => {
    const handler = () => {
      try {
        window.sessionStorage.setItem(SAW_FIRST_TESTCASE_KEY, "1");
      } catch {
        // sessionStorage may be unavailable in some environments
      }
      setHasShownNudge(true);
    };
    window.addEventListener("billing:test-case-saved", handler);
    return () => window.removeEventListener("billing:test-case-saved", handler);
  }, []);

  const handleDismiss = useCallback(() => {
    try {
      window.sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // ignore
    }
    setDismissed(true);
  }, []);

  if (!isAuthenticated) return null;
  if (!isOrgAdmin) return null;
  if (dismissed) return null;
  if (!hasShownNudge) return null;

  const planCode = data?.data?.planCode;
  if (!planCode || planCode !== "free") return null;

  return (
    <div
      role="region"
      aria-label="Upgrade prompt"
      className="sticky top-0 z-30 flex items-center justify-between gap-3 px-4 py-2.5 bg-primary text-primary-foreground border-b border-primary/30"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Sparkles size={16} aria-hidden="true" className="shrink-0" />
        <p className="text-sm leading-tight">
          You're on the Free plan — upgrade to add unlimited test cases and
          unlock AI.
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={() => navigate("/pricing")}
          className="inline-flex items-center justify-center px-3 py-1.5 rounded-md text-sm font-semibold underline-offset-2 hover:underline cursor-pointer min-h-[44px] min-w-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          See plans
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss upgrade banner"
          className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-white/15 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default UpgradeNudgeBanner;
